import { useQueryClient } from "@tanstack/react-query";
import Axios from "axios";

export const AUTH_TOKEN_KEY = "gc_authentication_token";
export const useTokenAuth = import.meta.env.VITE_TOKEN_AUTH === "true";

export const apiClient = Axios.create({
  baseURL: import.meta.env.VITE_API_URL || window.gc.global.api_url,
  headers: {
    "X-Gc-Client": import.meta.env.VITE_API_KEY || window.gc.global.api_key,
    ...(!useTokenAuth && { "X-Gc-Identmode": "cookie" }),
  },
  withCredentials: true,
});

if (useTokenAuth) {
  // In standalone dev mode, attach Bearer token from localStorage per-request.
  apiClient.interceptors.request.use(
    (config) => {
      try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // localStorage unavailable
      }
      return config;
    },
    null,
    { synchronous: true },
  );

  // Clear stale bearer tokens on 401, but only if the token used in the
  // failed request is still the one stored — avoids wiping a freshly
  // acquired token when a stale request's 401 arrives late.
  apiClient.interceptors.response.use(undefined, (error) => {
    if (Axios.isAxiosError(error) && error.response?.status === 401) {
      try {
        const failedToken = error.config?.headers?.Authorization;
        const stored = localStorage.getItem(AUTH_TOKEN_KEY);
        if (stored && failedToken === `Bearer ${stored}`) {
          localStorage.removeItem(AUTH_TOKEN_KEY);
        }
      } catch {
        // localStorage unavailable
      }
    }
    return Promise.reject(error);
  });
}

// Attach Mixpanel replay ID if available.
apiClient.interceptors.request.use(
  (config) => {
    // @ts-expect-error types out of date
    const session = window.mixpanel?.get_session_recording_properties();
    const $replayId = session?.$mp_replay_id;
    if ($replayId) {
      config.headers["mp-replay-id"] = $replayId;
    }
    return config;
  },
  null,
  { synchronous: true },
);

export function useRefetchQueries(
  ...keys: (string | number | undefined)[][]
): () => Promise<any[]> {
  const queryClient = useQueryClient();

  return async () => {
    const queries = keys.map((queryKey) => {
      return queryClient.refetchQueries({ queryKey, exact: true });
    });

    await Promise.all(queries);
    return keys.map((queryKey) => queryClient.getQueryData(queryKey));
  };
}

export function useInvalidateQueries(
  ...keys: (string | number | undefined)[][]
): () => Promise<void[]> {
  const queryClient = useQueryClient();

  return () => {
    const queries = keys.map((queryKey) => {
      return queryClient.invalidateQueries({ queryKey });
    });

    return Promise.all(queries);
  };
}

export function useRemoveQueries(
  ...keys: (string | number | undefined)[][]
): () => void {
  const queryClient = useQueryClient();

  return () => {
    keys.forEach((queryKey) => {
      queryClient.removeQueries({ queryKey });
    });
  };
}
